import { useMemo, useReducer, useState } from 'react';
import { useOverlayContext } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import typography from 'seagull/foundations/typography.module.scss';
import { createPaymentSetupHandlers } from '~/components/ManagePaymentMethods/ManagePaymentMethods.utils';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { handleRequest } from '~/infra/utils';
import { RelationshipProgressStatus } from '~/models/relationship';
import { selectors } from '~/store/state-selectors';
import { SlackUserGroups } from '~/utils/slack-client';
import { emptyCardNumberError, emptyCvvError, emptyExpiryError, getInitialStripeValue, getInitialValueState, mapStripeErrorToMessage, stripeStateReducer, validateNameOnCard, validateZipCode } from './ConnectCreditCardShared';
import { getGeneralErrorText } from './errors';
import mpmStyles from './ManagePaymentMethods.module.scss';
// https://stripe.com/docs/js/elements_object/create_element?type=card
const classesObj = {
    complete: mpmStyles.complete
};
export function useConnectCreditCard(props) {
    const { relationshipId, vendorId, customerId, disabledReason, updateProgress, onConnect, doPaymentMethodAction } = props;
    const { slackClient: { sendCsNotificationsMessage }, reactStripe: { useElements, useStripe } } = useIoContext();
    const elements = useElements();
    const stripe = useStripe();
    const overlayContext = useOverlayContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { api, logBI } = useStoreActions();
    const stripeLoadingFailed = useStore((state)=>state.stripeLoadingFailed);
    const [cardNum, cardNumDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCardNumberError));
    const [expiry, expiryDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyExpiryError));
    const [cvv, cvvDispatch] = useReducer(stripeStateReducer, getInitialStripeValue(emptyCvvError));
    const [nameOnCard, setNameOnCard] = useState(getInitialValueState);
    const [zipCode, setZipCode] = useState(getInitialValueState);
    const [apiError, setApiError] = useState();
    const [submitting, setSubmitting] = useState(false);
    const allFieldsValid = cardNum.valid && expiry.valid && cvv.valid && !nameOnCard.pristine && !nameOnCard.error && !zipCode.pristine && !zipCode.error;
    const styleObj = useMemo(()=>({
            base: {
                fontFamily: typography.fontFamilyLight,
                fontSize: typography.fontSizeP1,
                fontSmoothing: 'antialiased',
                color: colors.dark700,
                ':-webkit-autofill': {
                    color: colors.dark700
                },
                '::placeholder': {
                    color: colors.dark500
                }
            },
            invalid: {
                color: colors.dark700
            }
        }), []);
    const connectCreditCard = async ()=>{
        setApiError(undefined);
        if (!allFieldsValid || !elements || !stripe) return;
        const cardNumberEl = elements.getElement('cardNumber');
        if (!cardNumberEl) return;
        logBI(10806);
        const loaderName = 'creditCard';
        setSubmitting(true);
        overlayContext.startLoader(loaderName);
        const handleError = (message)=>{
            setSubmitting(false);
            overlayContext.stopLoader(loaderName);
            setApiError(message != null ? message : getGeneralErrorText());
            logBI(10819);
        };
        const { createSetupIntent, addToBusiness } = createPaymentSetupHandlers({
            isVendor,
            paymentMethodHandlers: api.paymentMethod,
            vendorId,
            customerId
        });
        const clientSecret = await handleRequest(createSetupIntent());
        if (!clientSecret.success) {
            return handleError();
        }
        const setupIntentResult = await stripe.confirmCardSetup(clientSecret.data, {
            payment_method: {
                card: cardNumberEl,
                billing_details: {
                    name: nameOnCard.value,
                    address: {
                        postal_code: zipCode.value
                    }
                }
            }
        });
        if (setupIntentResult.error) {
            const mappedMessage = mapStripeErrorToMessage(setupIntentResult.error);
            if (mappedMessage == null && setupIntentResult.error.code) {
                const text = "add credit-card error (relationshipId=".concat(relationshipId, ", vendorId=").concat(vendorId, ") - ").concat(JSON.stringify(setupIntentResult.error), " - ").concat(SlackUserGroups.ProductTeam, " please provide texts for this failure :pray:");
                sendCsNotificationsMessage(text);
            }
            logBI(10007, {
                success: false
            });
            return handleError(mappedMessage);
        }
        logBI(10007, {
            success: true
        });
        const paymentMethodId = setupIntentResult.setupIntent.payment_method;
        if (!paymentMethodId) {
            return handleError();
        }
        const fundingSource = await handleRequest(addToBusiness(paymentMethodId));
        if (!fundingSource.success) {
            return handleError();
        }
        if (updateProgress) {
            api.relationship.updateProgressStatus(relationshipId, RelationshipProgressStatus.PaymentMethod);
        }
        logBI(10808);
        overlayContext.stopLoader(loaderName);
        setSubmitting(false);
        onConnect(fundingSource.data.id);
    };
    return {
        cardNum,
        cardNumDispatch,
        expiry,
        expiryDispatch,
        cvv,
        cvvDispatch,
        nameOnCard,
        setNameOnCard,
        zipCode,
        setZipCode,
        apiError,
        setApiError,
        submitting,
        allFieldsValid,
        disabledReason,
        doPaymentMethodAction,
        stripeLoadingFailed,
        styleObj,
        classesObj,
        validateNameOnCard,
        validateZipCode,
        connectCreditCard
    };
}
